import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {add, format, formatISO, parseISO, parseJSON} from 'date-fns';
import toast from 'react-hot-toast';

import {
  ListResponse,
  Order,
  Vehicle,
  RouteStatus,
  Driver,
  SelectOption,
} from 'types';
import api from 'api';
import {wait} from 'utils/dummy';

import useStore from './store';
import {useHistory, useLocation} from 'react-router-dom';
import useOrderList from 'hooks/useOrderList';
import {get, reject} from 'lodash';
import {FilterOption} from './components/Filter/Filter';
import {PermissionRef} from 'components/PermissionModal';
import useDebounce from 'hooks/useDebounce';
import useAuth from 'hooks/useAuth';
import authContext from 'contexts/authContext';
import {getRoutes} from 'pages/Routes/useRoutes';
import {LatLngExpression} from 'leaflet';
import {useForm, useFormState} from 'react-hook-form';
import {DropResult} from 'react-beautiful-dnd';
import {useCompanyPlan} from 'hooks/useCompanyPlan';
import {INBOX_SORT_OPTIONS} from 'constants/select-options';

export interface Route {
  id: number | string;
  driver?: Driver;
  team: number;
  start_time: string | undefined;
  start_location: string;
  end_location: string;
  route_name: string;
  orders: Order[];
  deliveries: Order[];
  status: RouteStatus;
  total_time?: number;
  total_distance?: number; // in kilometers. should be convert to miles
  isClear?: boolean;
  start_latitude: number;
  start_longitude: number;
  end_latitude: number;
  end_longitude: number;
  polylines?: LatLngExpression[];
}

interface DriverExluded extends Omit<Route, 'driver'> {}

export interface ModifiedRoute extends DriverExluded {
  driver: number;
}
export interface DriverRoute {
  id: number;
  note: string;
}

export type Assignee = Driver | Vehicle;

const METERS_IN_MILE = 1609.34;

function roundToNearest30Minutes(roundedDate: Date) {
  const minutes = roundedDate.getMinutes();

  // Calculate the difference to the next 15-minute interval
  const remainder = minutes % 30;
  const difference = 30 - remainder;

  // Add the difference to the current time
  roundedDate.setMinutes(minutes + difference);

  return roundedDate;
}

const roundedTime = roundToNearest30Minutes(new Date());

let selectedTime = roundedTime.toISOString();
const parsedDefaultRoute = JSON.parse(
  sessionStorage.getItem('default_route') || '{}'
);
let defaultRoute: ModifiedRoute =
  parsedDefaultRoute && parsedDefaultRoute?.id
    ? parsedDefaultRoute
    : {
        id: '0',
        team: 0,
        driver: 0,
        start_time:
          format(Date.now(), 'yyyy-MM-dd') + selectedTime.slice(10, 19) + 'Z',

        route_name: '',
        orders: [],
        deliveries: [],
        status: 'new',
        start_location: '',
        end_location: '',
        start_latitude: 0,
        start_longitude: 0,
        end_latitude: 0,
        end_longitude: 0,
      };

function getRequestData(route: ModifiedRoute) {
  return {
    ...route,
    orders: [],
    deliveries: route.deliveries.map((o) => o.id),
  };
}
function getRouteCreateData(route: ModifiedRoute) {
  return {
    ...route,
    id: undefined,
    orders: [],
    deliveries: route.deliveries.map((o) => o.id),
  };
}

async function getVehicles() {
  const {data} = await api.get<ListResponse<Vehicle>>('/teams/', {
    params: {dispatch: true},
  });
  return data;
}

async function getWorkers() {
  const {data} = await api.get<ListResponse<Driver>>('/users/', {
    params: {role: 'driver'},
  });
  return data;
}
async function getRouteById({queryKey}: QueryFunctionContext) {
  const [, routeId] = queryKey;
  if (routeId && routeId !== '0') {
    const {data} = await api.get<ModifiedRoute>(`/routes/${routeId}/`);
    return {
      ...data,
      team: get(data, 'team.id', 0),
      driver: get(data, 'driver.id') ?? 0,
    };
  }

  return defaultRoute;
}

async function sendDispatchNote(dispatchNote: DriverRoute) {
  return await api.post(`/routes/deliveries/staff_note/${dispatchNote.id}/`, {
    staff_note: dispatchNote.note,
  });
}

function useDeliveryPlanner() {
  const {search, pathname, state} = useLocation();
  const [sortBy, setSortBy] = useState<SelectOption>(INBOX_SORT_OPTIONS[0]);
  const {currentUser} = useAuth();
  const {isSingleDispatch, isMultiDispatch, isAiDispatch} = useCompanyPlan();
  const {push} = useHistory();

  const routeId =
    useMemo(() => new URLSearchParams(search).get('id'), [search]) ||
    sessionStorage.getItem('route_id') ||
    '0';

  const routeIdRef = useRef(routeId);
  const isInitial = useRef(true);
  const permissionRef = useRef<PermissionRef>();
  useEffect(() => {
    if (routeIdRef.current !== '0') {
      push({search: `?id=${routeIdRef.current}`});
      showMap();
      // setMapSize((prev) => ({...prev, height: window.innerHeight - 350}));
    }
  }, [push]);

  useEffect(() => {
    if (!search && routeIdRef.current !== '0') {
      if (!isInitial.current) {
        routeIdRef.current = '0';
        sessionStorage.setItem('route_id', '0');
      }
    }
    isInitial.current = false;
  }, [search]);

  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState({
    location: [] as FilterOption[],
    date: undefined,
    time: undefined,
    priority: undefined,
  });
  const [location, setLocation] = useState<FilterOption[]>([]);
  const [date, setDate] = useState<undefined>();
  const [searchKey, setSearch] = useState<string>('');
  const [preferenceTime, setPreferenceTime] = useState<
    undefined | {value: string; label: string}
  >();
  const [priority, setPriority] = useState<SelectOption | undefined>();
  const [allCheckedAssign, setAllCheckedAssign] = useState(false);
  const [allChecked, setAllChecked] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [mapSize, setMapSize] = useState({
    width: window.innerWidth - 362,
    height: 0,
  });
  const shouldRecalculateInitialValue = sessionStorage.getItem(
    'should_recalculate_for_estimated_time'
  )
    ? JSON.parse(
        sessionStorage.getItem('should_recalculate_for_estimated_time') || ''
      )
    : true;

  const [
    shouldRecalculateForEstimatedTime,
    setShouldRecalculateForEstimatedTime,
  ] = useState(shouldRecalculateInitialValue);

  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(0);
  const [invalidOrderIds, setInvalidOrderIds] = useState<number[]>([]);
  useEffect(() => {
    const handleResize = () => {
      setMapSize((pr) => ({
        ...pr,
        width: window.innerWidth - 362,
      }));
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const distance = useStore(useCallback((state) => state.distance, []));

  const queryClient = useQueryClient();
  const {data: teams} = useQuery('teams', getVehicles);
  const {data: workers} = useQuery(['workers'], getWorkers);
  const searchDebounced = useDebounce(searchKey, 400);

  const {
    remove,
    refetch: refetchOrder,
    orders: data,
    isLoading,
    hasNextPage,
    isFetching,
    count,
    fetchNextPage,
  } = useOrderList({
    filter: location,
    unAssignedOnly: true,
    preference_date: date,
    preference_time: preferenceTime
      ? preferenceTime.value.toLowerCase()
      : undefined,
    priority: priority ? String(priority.value) : undefined,
    search: searchDebounced ? searchDebounced : undefined,
    sortBy: sortBy.value as string,
  });
  const [orders, setOrders] = useState(data);

  useEffect(() => {
    setOrders(data);
  }, [data]);

  const firstRender = useRef(true);

  useEffect(() => {
    if (!firstRender.current) {
      remove();
    }
    firstRender.current = false;
  }, [sortBy, remove]);

  useEffect(() => {
    if (!get(currentUser, 'company.plan')) {
      permissionRef.current?.open({
        action: '/settings/plans',
        buttonTitle: 'Buy plan',
        title:
          'You do not have any plan yet. You should buy plan for your business',
        cancelable: true,
      });
    } else if (get(currentUser, 'company.plan.plan_limit_out')) {
      permissionRef.current?.open({
        action: '/settings/billing',
        buttonTitle: 'Update plan',
        title: 'You have more active employee than your plan',
        cancelable: false,
      });
    }
  }, [state, permissionRef, currentUser]);

  let {data: route, refetch} = useQuery(['route', routeId], getRouteById, {
    refetchOnWindowFocus: Boolean(routeId),
    refetchInterval: 0,
  });

  const updateRoute = useMutation(
    // 'update_route',
    async function (nextRoute: ModifiedRoute) {
      const nextRouteData = getRequestData({
        ...route,
        ...nextRoute,
      });

      // save changes if route is already created
      if (route?.id && route?.id !== '0' && !nextRoute?.isClear) {
        const {data} = await api.put<ModifiedRoute>(`/routes/${route?.id}/`, {
          ...nextRouteData,
          driver: nextRouteData.driver,
        });
        return data;
      }

      // just return changes if route is not created
      return {
        ...route,
        ...nextRoute,
      };
    },

    {
      mutationKey: 'update_route',
      onMutate: async (nextRoute) => {
        await queryClient.cancelQueries(['route', routeId]);

        queryClient.setQueryData(['route', routeId], nextRoute);

        return nextRoute;
      },

      onSettled: (data) => {
        if (!data) {
          queryClient.invalidateQueries(['route', routeId]);
        }
      },
    }
  );

  const selectedWorker = useMemo(() => {
    const isIndividual = !!(route && route?.driver);
    const driver = route?.driver;
    const team = route?.team;
    if (isIndividual) {
      return workers?.results?.find((w) => w.driver_id === driver);
    } else {
      return teams?.results?.find((t) => t.id === team);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route?.team, route?.driver, route, workers, teams]);

  useEffect(() => {
    const workersRes = workers?.results;
    if (!selectedWorker && workersRes?.length) {
      if (route && !route.driver && !defaultRoute.driver) {
        const driver = workersRes?.[0]?.driver_id || 0;
        updateRoute.mutate({
          ...route,
          driver: Number(driver),
        });
        defaultRoute.driver = Number(driver);
      }
    }
  }, [workers, route?.driver, updateRoute, selectedWorker, route]);

  const handleChangeLocation = useCallback((value: any) => {
    if (value) {
      setFilter((pr) => ({
        ...pr,
        location: pr.location.some((item) => item.label === value.label)
          ? reject(pr.location, value)
          : [...pr.location, value],
      }));
    } else {
      setFilter((pr) => ({
        ...pr,
        location: [],
      }));
    }
  }, []);

  const onChangeData = useCallback((value: any) => {
    setFilter((pr) => ({
      ...pr,
      date: value,
    }));
  }, []);

  const onChangePriority = useCallback((value: any) => {
    setFilter((pr) => ({
      ...pr,
      priority: value,
    }));
  }, []);

  const [selectedUnassignedOrders, setSelectedUnassignedOrders] = useState<
    number[]
  >([]);
  const [selectedAssignedOrders, setSelectedAssignedOrders] = useState<
    number[]
  >([]);

  const unassignedOrders = useMemo(
    function () {
      if (orders) {
        const routeOrders = route?.deliveries.map((o) => o.id) || [];

        const unsorted = [...orders].filter((o) => !routeOrders.includes(o.id));
        return unsorted;
      }

      return [];
    },
    [orders, route?.deliveries]
  );

  useEffect(() => {
    if (route && route.deliveries.length < 1) {
      setAllCheckedAssign(false);
    }
    if (unassignedOrders.length < 1) {
      setAllChecked(false);
    }
  }, [route, fetchNextPage, unassignedOrders.length]);

  const handleSendRoute = useCallback(
    async (updatedRoute: ModifiedRoute) => {
      await api.post(`/routes/${updatedRoute.id}/send/`, {
        route_name: updatedRoute?.route_name,
      });
      push(pathname);
      sessionStorage.setItem('route_id', '0');

      defaultRoute.deliveries = [];
      sessionStorage.setItem('default_route', JSON.stringify(defaultRoute));
      updateRoute.mutate({
        ...updatedRoute,
        id: '0',
        status: 'new',
        deliveries: [],
        isClear: true,
        start_time: '',
        start_latitude: 0,
        start_longitude: 0,
        end_latitude: 0,
        end_longitude: 0,
        start_location: '',
        end_location: '',
      });
      defaultRoute.start_latitude = 0;
      defaultRoute.start_longitude = 0;
      defaultRoute.end_latitude = 0;
      defaultRoute.end_longitude = 0;
      defaultRoute.start_location = '';
      defaultRoute.end_location = '';
      sessionStorage.setItem('default_route', JSON.stringify(defaultRoute));

      setOrders(unassignedOrders);
      setSelectedAssignedOrders([]);

      queryClient.invalidateQueries(['orders']);
      hideMap();
    },
    [pathname, push, queryClient, unassignedOrders, updateRoute]
  );

  const showMap = () => {
    setMapSize((prev) => ({...prev, height: window.innerHeight - 580}));
  };
  const hideMap = () => {
    setMapSize((prev) => ({...prev, height: 0}));
  };
  function onChangeUnassignedCheckbox(id: number, checked: Boolean) {
    if (checked) {
      setSelectedUnassignedOrders([...selectedUnassignedOrders, id]);
    } else {
      setSelectedUnassignedOrders(
        selectedUnassignedOrders.filter((selected) => selected !== id)
      );
    }
  }

  const {setUser} = useContext(authContext);

  const [isFetchAll, setIsFetchAll] = useState(false);
  const [fetchingAll, setFetchingAll] = useState(false);

  const [activeOrder, setActiveOrder] = useState({});
  const [orderList, setOrderList] = useState<Order[] | null>(null);
  const [noteModal, setNoteModal] = useState(false);
  const [showGuide, setShowGuide] = useState(
    !get(currentUser, 'is_onboarded', false)
  );
  const [isSendRouteModalOpen, setIsSendRouteModalOpen] = useState(false);
  const [moveOrderModal, setMoveToOrder] = useState(false);
  const [routesPeriod, setRoutesPeriod] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: null,
    to: null,
  });

  const {data: routes} = useQuery(
    ['routes', String(currentPage - 1), 'created_at', '', '', routesPeriod],
    getRoutes
  );

  const checkedAll = useRef(false);

  const onboardingShown = useCallback(() => {
    if (currentUser) {
      setUser({
        ...currentUser,
        showGuide: false,
        is_onboarded: true,
      });
    }
    api
      .post('/users/onboard/', {
        is_onboarded: true,
      })
      .then(() => {})
      .catch((err) => {});
  }, [currentUser, setUser]);

  useEffect(() => {
    if (get(currentUser, 'showGuide', false)) {
      onboardingShown();
      setShowGuide(true);
    }
  }, [currentUser, onboardingShown]);

  useEffect(() => {
    (async () => {
      if (isFetchAll && hasNextPage) {
        setFetchingAll(true);
        try {
          await fetchNextPage();
        } catch (error) {}
      } else {
        setFetchingAll(false);
      }
    })();

    if (checkedAll.current) {
      setSelectedUnassignedOrders(orders.map((item) => item.id));
    }
  }, [isFetchAll, orders, hasNextPage, fetchNextPage]);

  async function onChangeUnassignedCheckboxAll(
    ids: number[],
    checked: boolean
  ) {
    if (checked) {
      setSelectedUnassignedOrders(ids);
    } else {
      setSelectedUnassignedOrders([]);
    }

    checkedAll.current = checked;
    setIsFetchAll(true);
  }

  function onChangeAssignedCheckbox(id: number, checked: boolean) {
    if (checked) {
      setSelectedAssignedOrders([...selectedAssignedOrders, id]);
    } else {
      setSelectedAssignedOrders(
        selectedAssignedOrders.filter((selected) => selected !== id)
      );
    }
  }

  function onChangeAssignedCheckboxAll(ids: number[], checked: boolean) {
    if (checked) {
      setSelectedAssignedOrders(ids);
    } else {
      setSelectedAssignedOrders([]);
    }
  }

  async function onChangeRouteParams(location: {
    name: string;
    lat: number;
    lng: number;
    type: 'start' | 'end';
  }) {
    if (!route) return;

    const data =
      location.type === 'start'
        ? {
            start_location: location.name,
            start_latitude: location.lat,
            start_longitude: location.lng,
            end_location: defaultRoute.end_location,
            end_latitude: defaultRoute.end_latitude,
            end_longitude: defaultRoute.end_longitude,
          }
        : {
            start_location: defaultRoute.start_location,
            start_latitude: defaultRoute.start_latitude,
            start_longitude: defaultRoute.start_longitude,
            end_location: location.name,
            end_latitude: location.lat,
            end_longitude: location.lng,
          };

    defaultRoute = {
      ...defaultRoute,
      ...data,
    };

    updateRoute.mutateAsync({
      ...route,
      ...data,
    });
    setShouldRecalculateForEstimatedTime(true);
    sessionStorage.setItem('should_recalculate_for_estimated_time', 'true');
  }

  async function onChangeStartTime(value: Date) {
    if (!route) return;

    defaultRoute.start_time = value ? formatISO(value) : undefined;

    await updateRoute.mutateAsync({
      ...route,
      start_time: value ? formatISO(value) : undefined,
    });
    setShouldRecalculateForEstimatedTime(true);
    sessionStorage.setItem('should_recalculate_for_estimated_time', 'true');
  }

  function assignSelected() {
    if (!route) return;
    if (!selectedWorker) {
      toast.error('Please select a worker or team');
      return;
    }
    updateRoute.mutate({
      ...route,
      deliveries: [
        ...route.deliveries,
        ...unassignedOrders.filter((o) =>
          selectedUnassignedOrders.includes(o.id)
        ),
      ],
    });

    defaultRoute.deliveries = [
      ...route.deliveries,
      ...unassignedOrders.filter((o) =>
        selectedUnassignedOrders.includes(o.id)
      ),
    ];

    if (hasNextPage) {
      fetchNextPage();
    }

    setSelectedUnassignedOrders([]);
    setShouldRecalculateForEstimatedTime(true);
    sessionStorage.setItem('should_recalculate_for_estimated_time', 'true');
  }

  async function moveToOrders() {
    if (!route) return;

    try {
      await api.post(`/routes/deliveries/move-to-orders/`, {
        order_ids:
          selectedOrder !== 0 ? [selectedOrder] : selectedUnassignedOrders,
      });
      refetchOrder();
      setSelectedOrder(0);
      setMoveToOrder(false);
      setOrders((pr) =>
        pr.filter((item) => !selectedUnassignedOrders.includes(item.id))
      );
      setSelectedUnassignedOrders([]);
    } catch (error) {}
  }

  function unassignSelected() {
    if (!route) return;

    defaultRoute.deliveries = route?.deliveries.filter(
      (o) => !selectedAssignedOrders.includes(o.id)
    );
    updateRoute.mutate({
      ...route,
      deliveries: route?.deliveries.filter(
        (o) => !selectedAssignedOrders.includes(o.id)
      ),
    });

    setSelectedAssignedOrders([]);
  }
  const [calculateType, setCalculateType] = useState<'ai' | 'manual' | 'none'>(
    'none'
  );
  const resetCalculate = () => {
    setCalculateType('none');
    push({search: ''});
    sessionStorage.setItem('route_id', '0');
    if (route)
      updateRoute.mutate({
        ...route,
        deliveries: [],
        status: 'new',
        start_time: undefined,
        start_location: '',
        end_location: '',
        start_latitude: 0,
        start_longitude: 0,
        end_latitude: 0,
        end_longitude: 0,
      });
    defaultRoute.deliveries = [];
    defaultRoute.start_location = '';
    defaultRoute.end_location = '';
    defaultRoute.start_latitude = 0;
    defaultRoute.start_longitude = 0;
    defaultRoute.end_latitude = 0;
    defaultRoute.end_longitude = 0;
    hideMap();
    sessionStorage.setItem('default_route', JSON.stringify(defaultRoute));
  };

  async function calculate({
    optimize_route = false,
  }: {
    optimize_route?: boolean;
  }) {
    let id = route?.id;

    if (!route?.start_location) {
      return toast.error(
        'Address or team was not assigned, Please assign address and team!'
      );
    }
   
    // if route is not created, create it
    if (route && (!route.id || route.id === '0')) {
      try {
        const {data} = await api.post<Route>(
          '/routes/',
          getRouteCreateData(route)
        );
        id = data.id;
       
      } catch (error) {
        if (get(error, 'response.status') === 426) {
          permissionRef.current?.open({
            action: '/settings/billing',
            buttonTitle: 'Updata plan',
            title: get(error, 'response.data.error', ''),
            cancelable: true,
          });
          return;
        }

        toast.error('Error while creating route, Please try again!');
      }
      //  finally {
      //   if (!optimize_route) {
      //     setCalculateType('none');
      //   }
      // }
    }
    if (id === '0') {
      return;
    }
    // if (id && !optimize_route) {
    //   defaultRoute.deliveries = [];
    //   sessionStorage.setItem('default_route', JSON.stringify(defaultRoute));
    //   push({search: `?id=${id}`});
    //   showMap();
    //   routeIdRef.current = String(id);
    //   sessionStorage.setItem('route_id', String(id));
    //   setCalculateType('none');
    //   return;
    // }
    const order_ids = route?.deliveries.map((order) =>
      String(get(order, 'id', ''))
    );
    // initiate task to calculate the route
    try {
      await api.post(`/routes/${id}/calculate/`, {
        optimize_route,
        order_ids,
        start_location: route?.start_location,
        end_location: route?.end_location,
        start_time: defaultRoute.start_time,
        start_latitude: route.start_latitude,
        start_longitude: route.start_longitude,
        end_latitude: route.end_latitude,
        end_longitude: route.end_longitude,
        team: defaultRoute.team,
        driver: defaultRoute?.driver,
      });
    } catch (error) {
      setCalculateType('none');
      if (get(error, 'response.status') === 426) {
        permissionRef.current?.open({
          action: '/settings/billing',
          buttonTitle: 'Updata plan',
          title: get(error, 'response.data.error', ''),
          cancelable: true,
        });
        return;
      }
      if (get(error, 'response.status') === 400) {
        const message = get(
          error,
          'response.data.message',
          'Something went wrong'
        );
        toast.error(message);
        const invalidOrders = get(error, 'response.data.invalid_order_ids', []);
        if (invalidOrders.length > 0) {
          setInvalidOrderIds(invalidOrders);
        }
        return;
      }
    }
    let pendingRoute = route;

    setIsAnimating(true);
    do {
      await wait(5000);
      try {
        const {data} = await api.get<ModifiedRoute>(`/routes/${id}/`);
        pendingRoute = data;

        if (data.status === 'canceled') {
          toast.error(
            get(data, 'error_message', '') ||
              'Wrong address, Please check attached addresses!'
          );
        }
        if (data.status === 'processed') {
          await updateRoute.mutateAsync({
            ...data,
            team: Number(get(data, 'team.id', 0)),
            driver: get(data, 'driver.id') ?? 0,
            isClear: true,
          });
          showMap();
          setShouldRecalculateForEstimatedTime(false);
          sessionStorage.setItem(
            'should_recalculate_for_estimated_time',
            'false'
          );
        }
      } catch (error) {}
    } while (pendingRoute.status === 'processing');

    setCalculateType('none');

    setTimeout(() => {
      setIsAnimating(false);
    }, 1000);

    if (id) {
      defaultRoute.deliveries = [];
      push({search: `?id=${id}`});
      routeIdRef.current = String(id);
      sessionStorage.setItem('route_id', String(id));
      setInvalidOrderIds([]);
    }
  }

  const justShowOnMap = async () => {
    let id = route?.id;

    if (!route?.start_location) {
      return toast.error(
        'Address or team was not assigned, Please assign address and team!'
      );
    }
    // if route is not created, create it
    if (route && (!route.id || route.id === '0')) {
      try {
        const {data} = await api.post<Route>(
          '/routes/',
          getRouteCreateData(route)
        );
        id = data.id;
      } catch (error) {
        if (get(error, 'response.status') === 426) {
          permissionRef.current?.open({
            action: '/settings/billing',
            buttonTitle: 'Updata plan',
            title: get(error, 'response.data.error', ''),
            cancelable: true,
          });
          return;
        }

        toast.error('Error while creating route, Please try again!');
      } finally {
        setCalculateType('none');
      }
    }
    if (id === '0') {
      return;
    }
    if (id) {
      defaultRoute.deliveries = [];
      sessionStorage.setItem('default_route', JSON.stringify(defaultRoute));
      push({search: `?id=${id}`});
      showMap();
      routeIdRef.current = String(id);
      sessionStorage.setItem('route_id', String(id));
      setCalculateType('none');
      return;
    }
  };

  function statsCalculator(route: undefined | ModifiedRoute) {
    return {
      stops: route?.deliveries.length,
      distance: `${Math.round((distance * 10) / METERS_IN_MILE) / 10} mi`,
      total_time: route?.total_time
        ? `${Math.floor(route.total_time / 60)}h ${route.total_time % 60}m`
        : '',
      end_time:
        route?.start_time && route.total_time
          ? format(
              add(parseISO(route.start_time), {minutes: route.total_time}),
              'h:mm aaa'
            )
          : '-:-- --',
    };
  }

  const move = (
    source: Order[],
    destination: Order[],
    droppableSource: DropResult['source'],
    droppableDestination: DropResult['destination']
  ) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination?.index || 0, 0, removed);

    const result = {
      assigned_list: [] as Order[],
      unassigned_list: [] as Order[],
    };
    result[droppableSource.droppableId as keyof typeof result] = sourceClone;
    result[droppableDestination?.droppableId as keyof typeof result] =
      destClone;

    return result;
  };

  const reorder = (list: Order[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    if (!route) return;

    // Cannot reorder on inbox list.
    if (
      result.source.droppableId === result.destination.droppableId &&
      result.source.droppableId === 'unassigned_list'
    ) {
      return;
    }

    setShouldRecalculateForEstimatedTime(true);
    sessionStorage.setItem('should_recalculate_for_estimated_time', 'true');

    if (
      result.source.droppableId === result.destination.droppableId &&
      result.source.droppableId === 'assigned_list'
    ) {
      const newItems: Order[] = reorder(
        route.deliveries,
        result.source.index,
        result.destination.index
      );
      defaultRoute.deliveries = newItems;
      sessionStorage.setItem('default_route', JSON.stringify(defaultRoute));
      route.deliveries = newItems;
      await updateRoute.mutateAsync({
        ...route,
        deliveries: newItems,
      });
    } else {
      // const droppableId = result.source.droppableId;
      // const draggableIdTo = result.destination.droppableId;

      // if (
      //   draggableIdTo !== 'list1' &&
      //   droppableId !== 'list1' &&
      //   droppableId !== draggableIdTo
      // ) {
      //   const team1Id = droppableId.split('-')[2];
      //   const team2Id = draggableIdTo.split('-')[2];
      //   const orderId = Number(result.draggableId.split('-')[2]);
      //   const order = teamsOrders[team1Id]?.find((item) => item.id === orderId);

      //   setTeamsOrders((pr) => ({
      //     ...pr,
      //     [team1Id]: uniqBy(
      //       (pr[team1Id] || [])?.filter((item) => item.id !== orderId),
      //       'id'
      //     ),
      //     [team2Id]: uniqBy([...(pr[team2Id] || []), order], 'id'),
      //   }));
      //   return;
      // }

      // if (
      //   droppableId.startsWith('droppable-team') &&
      //   draggableIdTo === 'list1'
      // ) {
      //   const teamId = droppableId.split('-')[2];
      //   const orderId = Number(result.draggableId.split('-')[2]);
      //   const order = teamsOrders[teamId]?.find((item) => item.id === orderId);
      //   setTeamsOrders((pr) => ({
      //     ...pr,
      //     [teamId]: uniqBy(
      //       (pr[teamId] || [])?.filter((item) => item.id !== orderId),
      //       'id'
      //     ),
      //   }));

      //   if (order) unassignedOrders.push(order);
      //   return;
      // }

      // if (droppableId === 'list1') {
      //   const orderId = Number(result.draggableId);
      //   const filteredOrders = unassignedOrders.filter(
      //     (item) => item.id !== orderId
      //   );
      //   const teamId = result.destination.droppableId.split('-')[2];
      //   const oldOrders = teamsOrders[teamId] || [];
      //   const order = unassignedOrders.find((item) => item.id === orderId);
      //   setTeamsOrders((pr) => ({
      //     ...pr,
      //     [teamId]: uniqBy([...oldOrders, order], 'id'),
      //   }));

      //   unassignedOrders.length = 0;
      //   unassignedOrders.push(...filteredOrders);
      //   setSelectedUnassignedOrders((pr) =>
      //     pr.filter((item) => item !== orderId)
      //   );

      //   if (!teamsOrders?.[teamId] || teamsOrders?.[teamId]?.length < 1) {
      //     setTimeout(() => {
      //       setShowTeamDetails(true);
      //     }, 1000);
      //   }

      //   return;
      // }

      const res = move(
        result.source.droppableId === 'assigned_list'
          ? route.deliveries
          : unassignedOrders,
        result.destination.droppableId === 'assigned_list'
          ? route.deliveries
          : unassignedOrders,
        result.source,
        result.destination
      );

      defaultRoute.deliveries = res?.assigned_list;
      sessionStorage.setItem('default_route', JSON.stringify(defaultRoute));
      route.deliveries = res?.assigned_list;
      await updateRoute.mutateAsync({
        ...route,
        deliveries: res?.assigned_list,
      });
      if (
        result.source.droppableId === 'assigned_list' &&
        res.assigned_list.length === 0
      ) {
        resetCalculate();
      }
      // if (
      //   result.destination?.droppableId === 'assigned_list' &&
      //   route.deliveries.length === 1
      // ) {
      //   setShowTeamDetails(true);
      // }

      setSelectedUnassignedOrders((pr) =>
        pr.filter((item) => item !== Number(result.draggableId))
      );
      setSelectedAssignedOrders((pr) =>
        pr.filter((item) => item !== Number(result.draggableId))
      );
    }
  };

  const calculateHandler = async () => {
    if (!route) return;
    //if start and end location is not selected don't calculate route and open location select modal.
    if (!(route.start_location && route.end_location)) {
      toast.error('Please select start and end location');
      return;
    }
    if (isSingleDispatch || isMultiDispatch) {
      permissionRef.current?.open({
        action: '/settings/billing',
        buttonTitle: 'Update plan',
        title: 'You need to upgrade your plan to use AI Route',
        cancelable: true,
      });
      return;
    }
    if (isAiDispatch && route.deliveries.length > 25) {
      permissionRef.current?.open({
        action: '',
        buttonTitle: 'Got it',
        title:
          'You can only use 25 orders at the same time when isCalculating. You can use mapping to view direction.',
        cancelable: true,
      });
      return;
    }
    setCalculateType('ai');
    await calculate({optimize_route: true});
  };

  const handleOrdersClick =
    (order: Order) => (e: React.MouseEvent<HTMLDivElement>) => {
      setActiveOrder(order);
      setOrderList(order.orders);
      e.stopPropagation();
    };

  const handleOrdersClose = () => {
    setOrderList(null);
    setNoteModal(false);
  };

  const calculateWithoutOptimize = async () => {
    if (!(route?.start_location && route?.end_location)) {
      toast.error('Please select start and end location');
      return;
    }
    setCalculateType('manual');
    await calculate({optimize_route: false});
  };

  const onChangePreferenceTime = (val: any) => {
    setFilter((pr) => ({
      ...pr,
      time: val,
    }));
  };

  const onChangeCheckboxAssign = () => {
    if (!route) return;
    setAllCheckedAssign((pr) => !pr);
    onChangeAssignedCheckboxAll(
      route.deliveries.map((item) => item.id),
      !allCheckedAssign
    );
  };

  const onChangeCheckbox = () => {
    setAllChecked((pr) => !pr);
    onChangeUnassignedCheckboxAll(
      unassignedOrders.map((item) => item.id),
      !allChecked
    );
  };

  const {handleSubmit, control, register, setValue, reset} = useForm();
  const {isSubmitting: isNoteSubmitting} = useFormState({control});

  const handleNoteClick =
    (order: Order) => (e: React.MouseEvent<HTMLDivElement>) => {
      setNoteModal(true);
      setValue('dispatchNote', order?.staff_note);
      setValue('id', order?.id);
      setActiveOrder(order);
      e.stopPropagation();
    };
  const handleNoteModalClose = () => {
    setNoteModal(false);
  };

  const noteSubmit = async (value: any) => {
    const body = {
      id: Number(value?.id),
      note: String(value?.dispatchNote),
    };
    const res = await sendDispatchNote(body);
    if (res.status === 201) {
      reset();
      handleNoteModalClose();
      refetchOrder();
    }
  };

  const observer = useRef<IntersectionObserver>();
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, fetchNextPage, hasNextPage]
  );

  const orderListRef = useRef<HTMLDivElement>(null);

  const resetFilter = () => {
    setFilter({
      location: [],
      time: undefined,
      priority: undefined,
      date: undefined,
    });
    setLocation([]);
    setPreferenceTime(undefined);
    setDate(undefined);
    setPriority(undefined);
    setShowFilterModal(false);
  };

  const applyFilter = () => {
    setShowFilterModal(false);
    setLocation(filter.location);
    setPreferenceTime(filter.time);
    setDate(filter.date);
    setPriority(filter.priority);
  };

  const onDeleteClick = (id: number) => {
    setSelectedOrder(id);
    setMoveToOrder(true);
  };

  const onChangeWorker = async (workerOrTeam: Driver | Vehicle) => {
    const isIndividual = workerOrTeam && 'driver_id' in workerOrTeam;
    if (route) {
      if (isIndividual) {
        await updateRoute.mutateAsync({
          ...route,
          // @ts-ignore
          driver: workerOrTeam?.driver_id,
          team: 0,
        });
        defaultRoute.team = 0;
        defaultRoute.driver = workerOrTeam?.driver_id!;
        sessionStorage.setItem('default_route', JSON.stringify(defaultRoute));
      } else if (!isIndividual) {
        await updateRoute.mutateAsync({
          ...route,
          driver: 0,
          team: Number(workerOrTeam?.id),
        });
        defaultRoute.team = Number(workerOrTeam?.id);
        defaultRoute.driver = 0;
        sessionStorage.setItem('default_route', JSON.stringify(defaultRoute));
      }
    }
  };

  const onClickSendRouteButton = () => {
    if (!route) return;
    if (calculateType !== 'none') {
      return;
    }

    if (invalidOrderIds.length) {
      toast.error('Please fix errors in the orders');
      return;
    }
    if (!route.deliveries.length) {
      toast.error('Please select at least one order to send');
      return;
    }
    if (!route.start_location || !route.end_location) {
      toast.error('Please select start and end location');
      return;
    }
    setIsSendRouteModalOpen(true);
  };

  const isSubmittable = Boolean(
    route && route.start_time && (route?.team || route?.driver)
  );

  const filtered =
    location.length > 0 ||
    !!preferenceTime?.value ||
    !!date ||
    !!priority?.value;

  return {
    filtered,
    resetFilter,
    applyFilter,
    route: route ? route : defaultRoute,
    currentTeam: teams?.results.find((v) => v.id === route?.team),
    teams: {
      results: teams?.results.filter((v) => !!v.id),
    },
    workers: {
      results: workers?.results.filter((v) => !!v.id),
    },

    unassignedOrders,
    isLoading,
    selectedUnassignedOrders,
    selectedAssignedOrders,

    stats: statsCalculator(route),

    isSubmittable,
    onClickSendRouteButton,
    onChangeUnassignedCheckbox,
    onChangeUnassignedCheckboxAll,
    onChangeAssignedCheckbox,
    onChangeAssignedCheckboxAll,
    onChangeRouteParams,
    onChangeStartTime,
    onSendRoute: handleSendRoute,
    count: count - (route?.deliveries.length || 0),
    sortBy,
    setSortBy,
    refetch,
    filter,
    handleChangeLocation,
    onChangeData,
    assignSelected,
    moveToOrders,
    unassignSelected,
    calculate,
    date,
    setPreferenceTime,
    preferenceTime,
    setSelectedUnassignedOrders,
    setSelectedAssignedOrders,
    currentPage,
    setCurrentPage,
    hasNextPage,
    fetchNextPage,
    isFetching,
    roundedTime: route?.start_time
      ? new Date(parseJSON(route?.start_time)).setHours(0, 0, 0, 0) >
        new Date().setHours(0, 0, 0, 0)
        ? undefined
        : roundedTime
      : roundedTime,
    setSearch,
    searchKey,
    fetchingAll,
    updateRoute,
    resetCalculate,
    search,
    defaultRoute,
    priority,
    onChangePriority,
    permissionRef,
    currentUser,
    handleDragEnd,
    activeOrder,
    setActiveOrder,
    orderList,
    handleOrdersClick,
    noteModal,
    setNoteModal,
    handleOrdersClose,
    allCheckedAssign,
    setAllCheckedAssign,
    allChecked,
    setAllChecked,
    calculateType,
    calculateHandler,
    justShowOnMap,
    showGuide,
    setShowGuide,
    calculateWithoutOptimize,
    onChangePreferenceTime,
    onChangeCheckboxAssign,
    onChangeCheckbox,
    isAnimating,
    isSendRouteModalOpen,
    setIsSendRouteModalOpen,
    moveOrderModal,
    setMoveToOrder,
    lastElementRef,
    orderListRef,
    showFilterModal,
    setShowFilterModal,
    mapSize,
    setMapSize,
    onDeleteClick,
    selectedWorker,
    routesPeriod,
    setRoutesPeriod,
    routes,
    onChangeWorker,
    handleSubmit,
    noteSubmit,
    isNoteSubmitting,
    register,
    handleNoteClick,
    handleNoteModalClose,
    shouldRecalculateForEstimatedTime,
    refetchOrders: refetchOrder,
    invalidOrderIds,
    isAiDispatch,
  };
}

export default useDeliveryPlanner;
